import { inject, Injectable } from '@angular/core';
import { Observable, of, forkJoin, switchMap, map, catchError } from 'rxjs';
import { EDisplayConditionType, EEqualityType, EDisplayConditionOperator, EDisplayState } from '@common/enums/display-condition.enum';
import { EduHttpService } from '@web-builder/core/services/REST/edu.http.service';
import type {
    BlockDisplayCondition,
    BlockDisplayConditionItem,
    ConditionContext,
    DateCondition,
    EduAuthCondition,
    EduGroupCondition,
    TimeCondition,
    UrlCondition,
    WeekdayCondition,
} from '@web/types';

import { dayjs } from '@common/helpers/dayjs';

const minutes = (m: dayjs.Dayjs) => m.hour() * 60 + m.minute();

@Injectable({
    providedIn: 'root',
})
export class BlockDisplayConditionService {
    private readonly eduHttpService: EduHttpService = inject(EduHttpService);

    public checkVisibility(blockDisplay: BlockDisplayCondition | null, context: ConditionContext): Observable<boolean | null> {
        if (!blockDisplay) {
            return of(true);
        }

        const hasBrowserCondition = blockDisplay.conditions.some(
            (condition: BlockDisplayConditionItem) =>
                condition.conditionType === EDisplayConditionType.URL ||
                condition.conditionType === EDisplayConditionType.GROUP_EDU ||
                condition.conditionType === EDisplayConditionType.AUTH_EDU
        );

        if (context.isServer && hasBrowserCondition) {
            return of(null);
        }

        const conditionChecks$: Observable<boolean | null>[] = blockDisplay.conditions.map((condition: BlockDisplayConditionItem) => {
            const { currentBrowserTime = dayjs.utc(), currentBrowserTimezone = 'UTC' } = context;
            return this.checkCondition(condition, { ...context, currentBrowserTime, currentBrowserTimezone });
        });

        return forkJoin(conditionChecks$).pipe(
            map((results) => {
                const validResults = results.filter((v): v is boolean => v !== null);

                if (!validResults.length) {
                    return false;
                }

                let conditionsPassed: boolean;

                if (blockDisplay.operator === EDisplayConditionOperator.AND) {
                    conditionsPassed = validResults.every((v) => v === true);
                } else if (blockDisplay.operator === EDisplayConditionOperator.OR) {
                    conditionsPassed = validResults.some((v) => v === true);
                } else {
                    conditionsPassed = false;
                }

                const booleanState = (blockDisplay.state || EDisplayState.VISIBLE) === EDisplayState.VISIBLE;
                return conditionsPassed === booleanState;
            }),
        );
    }

    private checkCondition(condition: BlockDisplayConditionItem, context: ConditionContext): Observable<boolean | null> {
        switch (condition.conditionType) {
            case EDisplayConditionType.ALWAYS:
                return of(true);

            case EDisplayConditionType.URL:
                return this.checkUrlCondition(condition, context);

            case EDisplayConditionType.DATE:
                return this.checkDateCondition(condition, context);

            case EDisplayConditionType.TIME:
                return this.checkTimeCondition(condition, context);

            case EDisplayConditionType.WEEKDAY:
                return this.checkWeekdayCondition(condition, context);

            case EDisplayConditionType.GROUP_EDU:
                return this.checkEduGroupCondition(condition, context);

            case EDisplayConditionType.AUTH_EDU:
                return this.checkEduAuthCondition(condition, context);

            default:
                return of(false);
        }
    }

    private checkUrlCondition(condition: UrlCondition, context: ConditionContext): Observable<boolean | null> {
        if (!context.isBrowser) {
            return of(null);
        }

        const currentLocation = new URL(window.location.href);
        const actualRaw = (currentLocation.search || '').replace(/^\?/, '').toLowerCase().trim();

        const expectedRaw = String(condition.value ?? '')
            .trim()
            .replace(/^\?/, '')
            .toLowerCase();

        if (condition.equality === EEqualityType.EQUAL) {
            return of(actualRaw === expectedRaw);
        }

        if (condition.equality === EEqualityType.NOT_EQUAL) {
            return of(actualRaw !== expectedRaw);
        }

        if (condition.equality === EEqualityType.CONTAINS) {
            return of(actualRaw.includes(expectedRaw));
        }

        return of(false);
    }

    private checkDateCondition(condition: DateCondition, context: ConditionContext): Observable<boolean> {
        const current = dayjs.utc();
        const [before, after] = condition.value.dateRange.map((t) => dayjs.utc(t));
        const isBetween = current.isBetween(before, after, 'date', '[]');

        return condition.equality === EEqualityType.EQUAL ? of(isBetween) : of(!isBetween);
    }

    private checkTimeCondition(
        condition: TimeCondition,
        { currentBrowserTime, currentBrowserTimezone }: ConditionContext,
    ): Observable<boolean> {
        const current = minutes(dayjs.tz(currentBrowserTime, currentBrowserTimezone).utc());
        const timeRange = condition.value.timeRange.map((t) => dayjs.utc(t));
        const [before, after] = timeRange.map((m) => minutes(m));

        let isBetween: boolean;
        if (before >= after) {
            // Wrap-around case: before is later in day than after (e.g., 23:00 to 06:00)
            // also 12:00 to 12:00 (exactly same time) is considered wrap-around
            // if 'current' is after 'before' that means its before midnight
            // if 'current' is before 'after' that means its after midnight
            isBetween = current >= before || current <= after;
        } else {
            isBetween = current >= before && current <= after;
        }

        return condition.equality === EEqualityType.EQUAL ? of(isBetween) : of(!isBetween);
    }

    private checkWeekdayCondition(
        condition: WeekdayCondition,
        { currentBrowserTime, currentBrowserTimezone }: ConditionContext,
    ): Observable<boolean> {
        const { dayCount, timeZone } = condition.value;

        // to be safe when timezone somehow is invalid
        try {
            const current = dayjs.utc().tz(timeZone).day();
            const isSameDay = current === dayCount;

            return condition.equality === EEqualityType.EQUAL ? of(isSameDay) : of(!isSameDay);
        } catch (error) {
            return of(false);
        }
    }

    private checkEduGroupCondition(condition: EduGroupCondition, context: ConditionContext): Observable<boolean> {
        if (!context.isAuthorizedStudent || !context.userId) {
            return of(false);
        }

        return this.eduHttpService.getStudentInfo().pipe(
            switchMap((studentInfo: any) => {
                const groupId = +condition.value.groupId;
                return this.eduHttpService.checkEduStudentGroupStatus(context.userId, studentInfo.id, [groupId]);
            }),
            map((isInGroups: boolean) => (condition.equality === EEqualityType.EQUAL ? isInGroups : !isInGroups)),
            catchError(() => of(false)),
        );
    }

    private checkEduAuthCondition(condition: EduAuthCondition, context: ConditionContext): Observable<boolean> {
        const loginState = condition.value === 'login';

        if (context.isAuthorizedStudent) {
            return this.eduHttpService.getStudentInfo().pipe(
                map((data) => loginState === Boolean(data)),
                catchError(() => of(false)),
            );
        }

        if (!context.isAuthorizedStudent && !loginState) {
            return of(true);
        }

        return of(false);
    }
}
